import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { billingApi, type BillingPlanId } from '@/lib/api'
import { useIsAuthenticated } from '@/store'

// ─── Nav ────────────────────────────────────────────────────────────────────

function NavBar() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-cosmic-mist/50 bg-cosmic-void/80 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-deep to-violet-mid flex items-center justify-center shadow-glow-violet">
            <span className="text-gold text-sm">✦</span>
          </div>
          <span className="font-display text-xl font-light text-star-white tracking-wide group-hover:text-gold transition-colors">
            Shubh Time
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/login" className="text-sm text-star-soft hover:text-star-bright transition-colors">
            Sign in
          </Link>
          <Link
            to="/login"
            className="text-sm px-4 py-1.5 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium hover:shadow-glow-violet transition-all"
          >
            Get started free
          </Link>
        </div>
      </div>
    </header>
  )
}

// ─── Types ───────────────────────────────────────────────────────────────────

type CtaVariant = 'primary' | 'outline'
type StreamType = 'b2c' | 'api'
type BillingCycle = 'monthly' | 'annual'

interface PricingTier {
  name: string
  price: string
  /** Shown below the price, e.g. "billed $89.99/yr" */
  subPrice?: string
  period: string
  description: string
  cta: string
  ctaVariant: CtaVariant
  highlight?: boolean
  badge?: string
  features: string[]
  /** undefined = free tier, goes to /login sign-up */
  planId?: BillingPlanId
  trial?: boolean
}

// ─── B2C Tiers ───────────────────────────────────────────────────────────────

const B2C_FREE: PricingTier = {
  name: 'Free',
  price: '$0',
  period: 'forever',
  description: 'Explore Vedic astrology at no cost',
  cta: 'Get started',
  ctaVariant: 'outline',
  features: [
    'Natal birth chart',
    'Basic Dasha timeline view',
    '3 Muhurta lookups per month',
    'Daily Choghadiya timings',
  ],
}

const B2C_PREMIUM_MONTHLY: PricingTier = {
  name: 'Premium',
  price: '$9.99',
  period: '/month',
  description: 'Full access for serious practitioners',
  cta: 'Start free trial',
  ctaVariant: 'primary',
  highlight: true,
  badge: 'Most popular',
  planId: 'premium_monthly',
  trial: true,
  features: [
    'Everything in Free',
    'Unlimited Muhurta lookups',
    'Full Dasha interpretation',
    'Compatibility (Ashtakoot) analysis',
    'Remedies & recommendations',
    'Calendar export (.ics)',
    'Push & email notifications',
  ],
}

const B2C_PREMIUM_ANNUAL: PricingTier = {
  name: 'Premium',
  price: '~$7.50',
  subPrice: 'billed $89.99/yr',
  period: '/month',
  description: 'Full access, ~25% off monthly',
  cta: 'Start free trial',
  ctaVariant: 'primary',
  highlight: true,
  badge: 'Save 25%',
  planId: 'premium_annual',
  trial: true,
  features: [
    'Everything in Free',
    'Unlimited Muhurta lookups',
    'Full Dasha interpretation',
    'Compatibility (Ashtakoot) analysis',
    'Remedies & recommendations',
    'Calendar export (.ics)',
    'Push & email notifications',
    'Priority email support',
    'Early access to new features',
  ],
}

// ─── API Tiers ────────────────────────────────────────────────────────────────

const API_TIERS: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Prototype and explore',
    cta: 'Get API key',
    ctaVariant: 'outline',
    features: [
      '100 requests / month',
      'All REST endpoints',
      'JSON responses',
      'Community support',
    ],
  },
  {
    name: 'Developer',
    price: '$29',
    period: '/month',
    description: 'For indie projects and startups',
    cta: 'Subscribe',
    ctaVariant: 'primary',
    highlight: true,
    badge: 'Most popular',
    planId: 'api_developer',
    features: [
      '10,000 requests / month',
      'All REST endpoints',
      'Webhook callbacks',
      'Email support',
      'Usage dashboard',
    ],
  },
  {
    name: 'Business',
    price: '$99',
    period: '/month',
    description: 'For production workloads',
    cta: 'Subscribe',
    ctaVariant: 'outline',
    planId: 'api_business',
    features: [
      '50,000 requests / month',
      'All REST endpoints',
      'Priority support (< 4h SLA)',
      '99.9% uptime SLA',
      'Usage analytics & alerting',
    ],
  },
]

// ─── Feature Comparison ───────────────────────────────────────────────────────

interface ComparisonRow {
  feature: string
  free: boolean | string
  premium: boolean | string
}

const B2C_COMPARISON: ComparisonRow[] = [
  { feature: 'Natal birth chart',          free: true,     premium: true },
  { feature: 'Daily Choghadiya',           free: true,     premium: true },
  { feature: 'Dasha timeline',             free: true,     premium: true },
  { feature: 'Muhurta lookups',            free: '3/mo',   premium: 'Unlimited' },
  { feature: 'Full Dasha interpretation',  free: false,    premium: true },
  { feature: 'Compatibility analysis',     free: false,    premium: true },
  { feature: 'Remedies',                   free: false,    premium: true },
  { feature: 'Calendar export (.ics)',     free: false,    premium: true },
  { feature: 'Push notifications',         free: false,    premium: true },
]

function ComparisonCell({ value }: { value: boolean | string }) {
  if (typeof value === 'string') {
    return <span className="text-gold text-sm font-mono">{value}</span>
  }
  return value ? (
    <span className="text-auspicious text-base">✓</span>
  ) : (
    <span className="text-star-dim text-sm">–</span>
  )
}

function FeatureComparisonTable({ rows }: { rows: ComparisonRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-cosmic-mist">
            <th className="text-left py-3 pr-4 text-star-muted font-normal">Feature</th>
            <th className="py-3 px-4 text-center font-semibold text-star-soft">Free</th>
            <th className="py-3 px-4 text-center font-semibold text-violet-glow">Premium</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.feature} className={`border-b border-cosmic-mist/40 ${i % 2 === 0 ? '' : 'bg-cosmic-nebula/30'}`}>
              <td className="py-3 pr-4 text-star-soft">{row.feature}</td>
              <td className="py-3 px-4 text-center"><ComparisonCell value={row.free} /></td>
              <td className="py-3 px-4 text-center"><ComparisonCell value={row.premium} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: 'What makes Shubh Time different from other astrology apps?',
    a: "We run every calculation through two independent engines — Swiss Ephemeris (the industry standard used by professional astrologers worldwide) and a secondary validation API. You get a result only when both agree within acceptable tolerance. This dual-validation approach is unique to Shubh Time.",
  },
  {
    q: 'Can I cancel at any time?',
    a: 'Yes. Monthly Premium can be cancelled any time — you keep access through the end of the billing period. Annual plans can be cancelled and will not auto-renew, but are non-refundable after 14 days.',
  },
  {
    q: 'Do I need a credit card for the free trial?',
    a: 'No credit card required for the 7-day free trial. You only enter payment details if you choose to continue after the trial period.',
  },
  {
    q: 'Do I need an account to use the API?',
    a: 'Yes, a free account is required to generate an API key. You can sign up with Google or Apple in under a minute.',
  },
  {
    q: 'How is the Muhurta calculation done?',
    a: 'We apply a 10-step Panchanga pipeline that evaluates Tithi, Vara, Nakshatra, Yoga, Karana, and five additional planetary factors. Each window is scored on a 0–100 scale based on classical Muhurta texts.',
  },
  {
    q: 'Is my birth chart data private?',
    a: 'Your chart data is stored securely and is never sold, shared, or used for advertising. You can delete your data at any time from Settings.',
  },
  {
    q: 'Do you offer Enterprise API plans?',
    a: 'Yes. Enterprise plans include unlimited requests, a dedicated SLA, priority engineering support, and custom integration help. Email enterprise@shubhtime.app to discuss.',
  },
]

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-cosmic-mist/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between py-5 text-left gap-4 group"
        aria-expanded={open}
      >
        <span className={`text-sm font-medium transition-colors ${open ? 'text-star-white' : 'text-star-bright'} group-hover:text-star-white`}>
          {q}
        </span>
        <span className={`text-gold mt-0.5 text-lg leading-none transition-transform shrink-0 ${open ? 'rotate-45' : ''}`} aria-hidden="true">
          +
        </span>
      </button>
      {open && (
        <div className="pb-5 pr-8 animate-fade-in">
          <p className="text-star-soft text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  )
}

// ─── Pricing Card ─────────────────────────────────────────────────────────────

function PricingCard({
  tier,
  onCtaClick,
  ctaLoading,
}: {
  tier: PricingTier
  onCtaClick?: () => void
  ctaLoading?: boolean
}) {
  const ctaClass = tier.ctaVariant === 'primary'
    ? 'bg-gradient-to-r from-violet to-violet-mid text-star-white hover:shadow-glow-violet disabled:opacity-60'
    : 'border border-cosmic-mist text-star-soft hover:border-violet-dark hover:text-star-bright disabled:opacity-60'

  return (
    <div
      className={`relative card-cosmic p-6 flex flex-col gap-5 ${
        tier.highlight
          ? 'border-violet/60 shadow-glow-violet ring-1 ring-violet/20'
          : ''
      }`}
    >
      {tier.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 rounded-full text-2xs font-mono bg-gradient-to-r from-violet to-violet-mid text-star-white shadow-glow-violet whitespace-nowrap">
            {tier.badge}
          </span>
        </div>
      )}

      <div>
        <h3 className="text-star-bright font-semibold mb-1">{tier.name}</h3>
        <p className="text-star-dim text-sm">{tier.description}</p>
      </div>

      <div className="flex flex-col gap-0.5">
        <div className="flex items-end gap-1">
          <span className="font-display text-4xl font-light text-star-white">{tier.price}</span>
          <span className="text-star-muted text-sm mb-1">{tier.period}</span>
        </div>
        {tier.subPrice && (
          <span className="text-star-dim text-xs font-mono">{tier.subPrice}</span>
        )}
      </div>

      {tier.planId ? (
        <button
          onClick={onCtaClick}
          disabled={ctaLoading}
          aria-label={`${tier.cta} — ${tier.name}`}
          className={`w-full text-center py-2.5 rounded-lg text-sm font-medium transition-all ${ctaClass}`}
        >
          {ctaLoading ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin" aria-hidden="true" />
              Loading…
            </span>
          ) : tier.cta}
        </button>
      ) : (
        <Link
          to="/login"
          aria-label={`${tier.cta} — ${tier.name}`}
          className={`w-full text-center py-2.5 rounded-lg text-sm font-medium transition-all ${ctaClass}`}
        >
          {tier.cta}
        </Link>
      )}

      <ul className="space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-star-soft">
            <span className="text-auspicious mt-0.5 shrink-0" aria-hidden="true">✓</span>
            {f}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PricingPage() {
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()

  const [activeStream, setActiveStream] = useState<StreamType>('b2c')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')
  const [checkoutLoading, setCheckoutLoading] = useState<BillingPlanId | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const b2cTiers: PricingTier[] = [
    B2C_FREE,
    billingCycle === 'monthly' ? B2C_PREMIUM_MONTHLY : B2C_PREMIUM_ANNUAL,
  ]

  async function handleCheckout(tier: PricingTier) {
    if (!tier.planId) return
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setCheckoutError(null)
    setCheckoutLoading(tier.planId)
    try {
      const { checkoutUrl } = await billingApi.checkout({
        planId: tier.planId,
        trial: tier.trial,
      })
      window.location.href = checkoutUrl
    } catch {
      setCheckoutError('Checkout is temporarily unavailable. Please try again shortly.')
      setCheckoutLoading(null)
    }
  }

  const tiers = activeStream === 'b2c' ? b2cTiers : API_TIERS

  return (
    <div className="min-h-screen bg-cosmic-void relative overflow-x-hidden">
      {/* Ambient glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 70% 50% at 50% -10%, rgba(107,33,168,0.18) 0%, transparent 60%),
            radial-gradient(ellipse 40% 40% at 90% 90%, rgba(59,7,100,0.15) 0%, transparent 60%)
          `,
        }}
        aria-hidden="true"
      />

      <div className="relative z-10">
        <NavBar />

        {/* Hero */}
        <section className="pt-32 pb-16 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-gold font-mono text-xs mb-4 tracking-widest">PRICING</p>
            <h1 className="font-display text-5xl font-light text-star-white mb-5 leading-tight">
              Simple, transparent pricing
            </h1>
            <p className="text-star-soft text-lg max-w-xl mx-auto leading-relaxed">
              Start free. Upgrade when you need more. No hidden fees, no surprise charges.
            </p>
          </div>
        </section>

        {/* Stream toggle */}
        <div className="flex justify-center px-6 mb-8">
          <div className="inline-flex rounded-xl border border-cosmic-mist bg-cosmic-nebula p-1 gap-1" role="tablist" aria-label="Pricing category">
            <button
              role="tab"
              aria-selected={activeStream === 'b2c'}
              onClick={() => setActiveStream('b2c')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                activeStream === 'b2c'
                  ? 'bg-gradient-to-r from-violet to-violet-mid text-star-white shadow-glow-violet'
                  : 'text-star-soft hover:text-star-bright'
              }`}
            >
              Web App
            </button>
            <button
              role="tab"
              aria-selected={activeStream === 'api'}
              onClick={() => setActiveStream('api')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                activeStream === 'api'
                  ? 'bg-gradient-to-r from-violet to-violet-mid text-star-white shadow-glow-violet'
                  : 'text-star-soft hover:text-star-bright'
              }`}
            >
              Developer API
            </button>
          </div>
        </div>

        {/* Monthly / Annual toggle — B2C only */}
        {activeStream === 'b2c' && (
          <div className="flex justify-center px-6 mb-12" aria-label="Billing cycle">
            <div className="inline-flex items-center gap-3">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`text-sm font-medium transition-colors ${
                  billingCycle === 'monthly' ? 'text-star-white' : 'text-star-dim hover:text-star-soft'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                aria-checked={billingCycle === 'annual'}
                role="switch"
                aria-label="Toggle annual billing"
                className={`relative w-11 h-6 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-violet ${
                  billingCycle === 'annual' ? 'bg-violet' : 'bg-cosmic-mist'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-star-white shadow transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-5' : ''
                  }`}
                />
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  billingCycle === 'annual' ? 'text-star-white' : 'text-star-dim hover:text-star-soft'
                }`}
              >
                Annual
                <span className="px-1.5 py-0.5 rounded text-2xs font-mono bg-auspicious/20 text-auspicious">
                  Save 25%
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Checkout error */}
        {checkoutError && (
          <div className="max-w-5xl mx-auto px-6 mb-6">
            <div
              role="alert"
              className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg bg-inauspicious/10 border border-inauspicious/30 text-inauspicious text-sm"
            >
              <span>{checkoutError}</span>
              <button
                onClick={() => setCheckoutError(null)}
                aria-label="Dismiss error"
                className="shrink-0 text-lg leading-none opacity-70 hover:opacity-100 transition-opacity"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Pricing cards */}
        <section className="px-6 mb-20">
          <div className="max-w-5xl mx-auto">
            <div className={`grid gap-6 ${tiers.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : 'md:grid-cols-3'}`}>
              {tiers.map((tier) => (
                <PricingCard
                  key={`${tier.name}-${tier.planId ?? 'free'}`}
                  tier={tier}
                  onCtaClick={() => handleCheckout(tier)}
                  ctaLoading={!!tier.planId && checkoutLoading === tier.planId}
                />
              ))}
            </div>

            {activeStream === 'api' && (
              <p className="text-center text-star-dim text-sm mt-6">
                Need more?{' '}
                <a href="mailto:enterprise@shubhtime.app" className="text-gold hover:text-gold-bright transition-colors">
                  Contact us for Enterprise pricing →
                </a>
              </p>
            )}

            {activeStream === 'b2c' && (
              <p className="text-center text-star-dim text-xs mt-4">
                7-day free trial · No credit card required · Cancel any time
              </p>
            )}
          </div>
        </section>

        {/* Feature comparison table (B2C only) */}
        {activeStream === 'b2c' && (
          <section className="px-6 mb-24">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-display text-2xl font-light text-star-white mb-6">
                Full feature comparison
              </h2>
              <div className="card-cosmic p-6">
                <FeatureComparisonTable rows={B2C_COMPARISON} />
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="px-6 pb-24 border-t border-cosmic-mist/30 pt-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-3xl font-light text-star-white mb-2 text-center">
              Frequently asked questions
            </h2>
            <p className="text-star-dim text-sm text-center mb-10">
              Can't find an answer?{' '}
              <a href="mailto:hello@shubhtime.app" className="text-gold hover:text-gold-bright transition-colors">
                Email us
              </a>
            </p>
            <div className="card-cosmic p-6 divide-y-0">
              {FAQS.map((faq) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="px-6 py-20 border-t border-cosmic-mist/30 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="font-display text-3xl font-light text-star-white mb-4">
              Ready to find your Shubh Time?
            </h2>
            <p className="text-star-soft mb-8">
              Free forever. No credit card. Start exploring in under a minute.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium hover:shadow-glow-violet transition-all active:scale-[0.98]"
            >
              Create free account
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-cosmic-mist/30 py-10 px-6">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-deep to-violet-mid flex items-center justify-center">
                <span className="text-gold text-xs">✦</span>
              </div>
              <span className="font-display text-lg text-star-soft">Shubh Time</span>
            </Link>
            <div className="flex items-center gap-6">
              <a href="/api-docs" className="text-xs text-star-dim hover:text-star-soft transition-colors">API Docs</a>
              <a href="/terms" className="text-xs text-star-dim hover:text-star-soft transition-colors">Terms</a>
              <a href="/privacy" className="text-xs text-star-dim hover:text-star-soft transition-colors">Privacy</a>
              <a href="mailto:hello@shubhtime.app" className="text-xs text-star-dim hover:text-star-soft transition-colors">Contact</a>
            </div>
            <p className="text-xs text-star-dim font-mono">© 2026 Shubh Time · शुभ समय</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
